import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { LAST_ACTIVITY_KEY } from "../domain/session-timeout";
import type { Credentials } from "../domain/types";
import { ApiError, createSession, endSession, getNodes } from "../lib/api";

const SESSION_KEY = "cloud4smart.session";

type AuthContextValue = {
  credentials: Credentials | null;
  login: (credentials: Credentials) => Promise<void>;
  logout: () => void;
  updateCredentials: (credentials: Credentials) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function restoreCredentials(): Credentials | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Credentials>;
    if (typeof parsed.login !== "string" || typeof parsed.password !== "string") return null;
    return { login: parsed.login, password: parsed.password };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [credentials, setCredentials] = useState<Credentials | null>(restoreCredentials);

  const updateCredentials = useCallback((next: Credentials) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setCredentials(next);
  }, []);

  const login = useCallback(
    async (next: Credentials) => {
      try {
        const sessionCredentials = await createSession(next);
        await getNodes(sessionCredentials);
        updateCredentials(sessionCredentials);
      } catch (error) {
        // Temporary compatibility with the currently deployed pre-session backend.
        // This branch disappears automatically after the new backend is deployed.
        if (!(error instanceof ApiError) || (error.status !== 404 && error.status !== 405)) throw error;
        await getNodes(next);
        updateCredentials(next);
      }
    },
    [updateCredentials],
  );

  const logout = useCallback(() => {
    void endSession().catch(() => undefined);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    setCredentials(null);
  }, []);

  useEffect(() => {
    const onExpired = () => logout();
    window.addEventListener("cloud-auth-expired", onExpired);
    return () => window.removeEventListener("cloud-auth-expired", onExpired);
  }, [logout]);


  const value = useMemo(
    () => ({ credentials, login, logout, updateCredentials }),
    [credentials, login, logout, updateCredentials],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

export function useCredentials(): Credentials {
  const { credentials } = useAuth();
  if (!credentials) throw new Error("Authenticated credentials are required");
  return credentials;
}
